import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSpiritualStore } from '../hooks/useSpiritualStore';
import { useSacredAudio } from '../hooks/useSacredAudio';
import { useTheme } from '../contexts/ThemeContext';
import { User, Sparkles, Heart, Brain, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import Fr00tLogo from '../components/Fr00tLogo';

// Sacred Welcome Page Component
// No capitalism - just a simple welcome and setup
const WelcomePage: React.FC = () => {
  const { colors, gradients, theme } = useTheme();
  const [username, setUsername] = useState('');
  const [spiritualPath, setSpiritualPath] = useState<'seeker' | 'practitioner' | 'teacher' | 'master'>('seeker');
  const navigate = useNavigate();
  
  const saveUsername = useSpiritualStore(s => s.setUsername);
  const savePath = useSpiritualStore(s => s.setSpiritualPath);
  const { 
    playSacredTone, 
    initializeAudio,
    isInitialized 
  } = useSacredAudio();

  // Initialize audio on component mount
  React.useEffect(() => {
    if (!isInitialized) {
      initializeAudio().catch(err => {
        console.error('Failed to initialize audio:', err);
      });
    }
  }, [isInitialized, initializeAudio]);

  const spiritualPaths = [
    { 
      id: 'seeker' as const, 
      name: 'Seeker', 
      icon: Sparkles,
      color: colors.amber,
      description: 'Beginning the journey of discovery'
    },
    { 
      id: 'practitioner' as const, 
      name: 'Practitioner', 
      icon: Heart,
      color: colors.emerald,
      description: 'Actively practicing spiritual work'
    },
    { 
      id: 'teacher' as const, 
      name: 'Teacher', 
      icon: Brain,
      color: colors.indigo,
      description: 'Sharing wisdom with others'
    },
    { 
      id: 'master' as const, 
      name: 'Master', 
      icon: Star,
      color: colors.rose,
      description: 'Embodying complete understanding'
    },
  ];

  const handleBegin = () => {
    if (!username.trim()) {
      toast.error('Please enter a name or pseudonym');
      return;
    }

    // Save locally - no server, no accounts
    saveUsername(username.trim());
    savePath(spiritualPath);
    
    // Play welcome tone
    if (isInitialized) {
      playSacredTone(528, 1.5); // Love frequency
    }
    
    toast.success(`🌟 Welcome, ${username}! Your journey begins...`);
    
    // Navigate to home
    setTimeout(() => {
      navigate('/');
    }, 500);
  };

  return (
    <div 
      className="fr00t-welcome-page"
      style={{
        minHeight: '100vh',
        background: `
          radial-gradient(circle at 20% 50%, #0a0a0a 0%, #000000 100%),
          linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)
        `
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}
      >
        <div style={{
          background: `
            linear-gradient(135deg, 
              #2d2d2d 0%, 
              #1e1e1e 25%, 
              #2a2a2a 50%, 
              #1e1e1e 75%, 
              #2d2d2d 100%
            )
          `,
          backdropFilter: 'blur(20px)',
          borderRadius: '4px',
          padding: '3rem',
          maxWidth: '600px',
          width: '100%',
          border: '2px solid #0a0a0a',
          boxShadow: `
            inset 0 1px 3px rgba(255,255,255,0.1),
            inset 0 -1px 3px rgba(0,0,0,0.5),
            0 4px 8px rgba(0,0,0,0.8),
            0 0 0 1px #404040,
            0 25px 50px -12px rgba(0, 0, 0, 0.25)
          `
        }}>
          {/* Sacred Header */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            style={{ textAlign: 'center', marginBottom: '2rem' }}
          >
            <h1 style={{
              fontSize: '2.5rem',
              background: 'linear-gradient(135deg, #a855f7, #ec4899)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '0.5rem',
              fontWeight: 'bold'
            }}>
              Welcome to Fr00t
            </h1>
            <p style={{ color: colors.gray, fontSize: '1.1rem' }}>
              A free creative science tool for everyone
            </p>
            <p style={{ color: colors.gray, fontSize: '0.9rem', marginTop: '1rem' }}>
              No accounts • No tracking • No capitalism<br/>
              Just pure exploration and creativity
            </p>
          </motion.div>

          {/* Sacred Numerology */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, type: "spring" }}
            style={{
              textAlign: 'center',
              padding: '1.5rem',
              background: `
                linear-gradient(135deg, 
                  #2d2d2d 0%, 
                  #1e1e1e 25%, 
                  #2a2a2a 50%, 
                  #1e1e1e 75%, 
                  #2d2d2d 100%
                )
              `,
              borderRadius: '4px',
              border: `2px solid ${colors.purple}`,
              boxShadow: `
                inset 0 1px 3px rgba(255,255,255,0.1),
                inset 0 -1px 3px rgba(0,0,0,0.5),
                0 2px 4px rgba(0,0,0,0.6),
                0 0 0 1px #404040,
                0 0 6px ${colors.purple}30
              `,
              marginBottom: '2rem'
            }}
          >
            <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'center' }}>
              <Fr00tLogo size={64} animated />
            </div>
            <div style={{ color: colors.purple, fontSize: '1.2rem', marginBottom: '0.5rem' }}>
              F(6) + R(9) + 0(0) + 0(0) + T(2) = 8
            </div>
            <div style={{ color: colors.gray, fontSize: '0.9rem' }}>
              Harmony • Wisdom • Infinity • Partnership • Abundance
            </div>
          </motion.div>

          {/* Name Input */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ marginBottom: '2rem' }}
          >
            <label style={{
              display: 'block',
              color: '#e2e8f0',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500
            }}>
              What shall we call you?
            </label>
            <div style={{ position: 'relative' }}>
              <User
                size={20}
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#8b5cf6',
                  opacity: 0.6
                }}
              />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter any name or pseudonym..."
                onKeyPress={(e) => e.key === 'Enter' && handleBegin()}
                style={{
                  width: '100%',
                  padding: '1rem 1rem 1rem 3rem',
                  background: `
                    linear-gradient(135deg, 
                      #2d2d2d 0%, 
                      #1e1e1e 25%, 
                      #2a2a2a 50%, 
                      #1e1e1e 75%, 
                      #2d2d2d 100%
                    )
                  `,
                  border: '2px solid #0a0a0a',
                  borderRadius: '4px',
                  boxShadow: `
                    inset 0 1px 3px rgba(255,255,255,0.1),
                    inset 0 -1px 3px rgba(0,0,0,0.5),
                    0 2px 4px rgba(0,0,0,0.6),
                    0 0 0 1px #404040
                  `,
                  color: '#f1f5f9',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>
            <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              Stored only in your browser. Never sent anywhere.
            </p>
          </motion.div>

          {/* Spiritual Path Selection */}
          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            style={{ marginBottom: '2rem' }}
          >
            <label style={{
              display: 'block',
              color: '#e2e8f0',
              marginBottom: '1rem',
              fontSize: '0.9rem',
              fontWeight: 500
            }}>
              Choose your path
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem'
            }}>
              {spiritualPaths.map((path) => {
                const Icon = path.icon;
                const isSelected = spiritualPath === path.id;
                return (
                  <motion.button
                    key={path.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setSpiritualPath(path.id);
                      if (isInitialized) {
                        playSacredTone(432 + path.id.length * 50, 0.3);
                      }
                    }}
                    style={{
                      padding: '1rem',
                      background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 25%, 
                        #2a2a2a 50%, 
                        #1e1e1e 75%, 
                        #2d2d2d 100%
                      )
                    `,
                      border: `2px solid ${isSelected ? path.color : '#0a0a0a'}`,
                      borderRadius: '4px',
                      boxShadow: `
                        inset 0 1px 3px rgba(255,255,255,0.1),
                        inset 0 -1px 3px rgba(0,0,0,0.5),
                        0 2px 4px rgba(0,0,0,0.6),
                        0 0 0 1px #404040
                        ${isSelected ? `, 0 0 6px ${path.color}40` : ''}
                      `,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textAlign: 'center',
                      color: '#f1f5f9'
                    }}
                  >
                    <Icon
                      size={32}
                      style={{
                        color: path.color,
                        margin: '0 auto 0.5rem',
                        display: 'block'
                      }}
                    />
                    <div style={{
                      fontWeight: 600,
                      marginBottom: '0.25rem',
                      color: path.color
                    }}>
                      {path.name}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#94a3b8'
                    }}>
                      {path.description}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>

          {/* Begin Button */}
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleBegin}
            style={{
              width: '100%',
              padding: '1rem',
              background: `
                linear-gradient(135deg, 
                  #2d2d2d 0%, 
                  #1e1e1e 25%, 
                  #2a2a2a 50%, 
                  #1e1e1e 75%, 
                  #2d2d2d 100%
                )
              `,
              border: `2px solid ${colors.purple}`,
              borderRadius: '4px',
              color: colors.purple,
              fontSize: '1.1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: `
                inset 0 1px 3px rgba(255,255,255,0.1),
                inset 0 -1px 3px rgba(0,0,0,0.5),
                0 4px 8px rgba(0,0,0,0.8),
                0 0 0 1px #404040,
                0 0 12px ${colors.purple}40,
                0 10px 30px -10px rgba(168, 85, 247, 0.5)
              `,
              marginBottom: '1rem',
              outline: 'none'
            }}
          >
            Begin Your Journey 🌟
          </motion.button>

          {/* Skip Option */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            onClick={() => navigate('/')}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: `
                linear-gradient(135deg, 
                  #2d2d2d 0%, 
                  #1e1e1e 50%, 
                  #2d2d2d 100%
                )
              `,
              border: '2px solid #0a0a0a',
              borderRadius: '4px',
              color: '#94a3b8',
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: `
                inset 0 1px 2px rgba(255,255,255,0.1),
                inset 0 -1px 2px rgba(0,0,0,0.5),
                0 2px 4px rgba(0,0,0,0.6),
                0 0 0 1px #404040
              `,
              outline: 'none'
            }}
          >
            Skip for now (explore as guest)
          </motion.button>

          {/* Sacred Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            style={{
              marginTop: '2rem',
              padding: '1rem',
              background: `
                linear-gradient(135deg, 
                  #2d2d2d 0%, 
                  #1e1e1e 50%, 
                  #2d2d2d 100%
                )
              `,
              borderRadius: '4px',
              border: `2px solid ${colors.emerald}`,
              boxShadow: `
                inset 0 1px 3px rgba(255,255,255,0.1),
                inset 0 -1px 3px rgba(0,0,0,0.5),
                0 2px 4px rgba(0,0,0,0.6),
                0 0 0 1px #404040,
                0 0 6px ${colors.emerald}30
              `,
              textAlign: 'center'
            }}
          >
            <p style={{ color: '#10b981', fontSize: '0.85rem', margin: 0 }}>
              ✨ Free forever • Open source • Built with love ✨<br/>
              <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                All data stays in your browser. No servers. No surveillance.
              </span>
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default WelcomePage;
